import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { useTenantBrand } from "@/context/TenantBrandContext";
import { usePlatformBranding, brandUrl } from "@/context/PlatformBrandingContext";
import BrandLogo from "@/components/common/BrandLogo";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { api, formatErr } from "@/lib/api";
import {
  FolderOpen, Settings2, LogOut, ChevronDown, ChevronRight, ChevronLeft, Menu, X, Check, CreditCard, User,
  ScrollText, Users as UsersIcon, LayoutGrid,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ProfileDialog from "@/components/ProfileDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import NotificationBell from "@/components/NotificationBell";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import { NAV_TOP, NAV_GROUPS, NAV_SECTIONS } from "@/components/layout/navConfig";
import { navAllowedForUser, navEntitled, navGroupAllowedForModule } from "@/components/layout/navVisibility";
import { isDualAccess, getPreferredWorkspace, setPreferredWorkspace } from "@/lib/workspaceAccess";
import SidebarGroup from "@/components/layout/SidebarGroup";
import SidebarSection from "@/components/layout/SidebarSection";

const LOGO = "/logo.svg";

// NAV_TOP + NAV_GROUPS are imported from `./layout/navConfig`.
// `navAllowedForUser` / `navEntitled` / `visibleItems` come from
// `./layout/navVisibility`. `SidebarGroup` + `ServicesTreeNode` are in
// their own modules under `./layout/`. Keeping this file focused on
// layout shell.

export default function Layout({ children }) {
  const { user, logout, canHR } = useAuth();
  const sub = useSubscription();
  const brand = useTenantBrand();
  // 2026-08-25 · Mirror the Login page's exact logo asset selection.
  // Login uses `platformBranding.login` (fallback: bundled /logo.svg).
  // Using the same source guarantees the sidebar mark and the login
  // mark are pixel-identical — the tenant symbol upload is
  // deliberately NOT consulted here.
  const platformBranding = usePlatformBranding();
  const LOGIN_LOGO = brandUrl(platformBranding?.login) || LOGO;
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [tenant, setTenant] = useState({ name: "", fy: "" });
  const [fys, setFys] = useState([]);
  const [activeFyId, setActiveFyId] = useState(null);
  const [fySwitching, setFySwitching] = useState(false);
  const canManageFY = user?.permissions?.includes?.("admin.manage_financial_year")
    || (user?.roles || []).some((r) => ["Owner", "Admin"].includes(r));

  const refreshFys = async () => {
    const fy = await api.get("/financial-years").catch(() => null);
    const list = Array.isArray(fy?.data?.data) ? fy.data.data : [];
    setFys(list);
    const active = list.find((f) => f.is_active) || list[0];
    setActiveFyId(active?.id || null);
    setTenant((t) => ({ ...t, fy: active?.name || "" }));
  };

  useEffect(() => {
    Promise.all([
      api.get("/company-profile").catch(() => null),
      api.get("/financial-years").catch(() => null),
    ]).then(([cp, fy]) => {
      const name = cp?.data?.data?.company_profile?.legal_name
        || cp?.data?.data?.organization?.name
        || "";
      const list = Array.isArray(fy?.data?.data) ? fy.data.data : [];
      const active = list.find((f) => f.is_active) || list[0];
      setFys(list);
      setActiveFyId(active?.id || null);
      setTenant({ name, fy: active?.name || "" });
    });
  }, []);

  const switchFy = async (fyId) => {
    if (!canManageFY) {
      toast.info("Only Owner/Admin can switch the global active FY. Open Admin Settings to manage.");
      return;
    }
    if (fyId === activeFyId) return;
    const target = fys.find((f) => f.id === fyId);
    if (!target) return;
    setFySwitching(true);
    try {
      await api.put(`/admin-settings/financial-years/${fyId}`, {
        ...target, is_active: true,
      });
      toast.success(`Switched active FY to ${target.name}. Reloading…`);
      await refreshFys();
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error(formatErr(e.response?.data) || e.message);
    } finally {
      setFySwitching(false);
    }
  };

  const initials = (user?.full_name || user?.email || "U")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  // Phase-F · sidebar expand/collapse width control. Persisted across reloads.
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    try { return window.localStorage.getItem("sidebar.expanded") === "1"; }
    catch (e) { return false; }
  });
  const toggleSidebarWidth = () => {
    setSidebarExpanded((prev) => {
      const next = !prev;
      try { window.localStorage.setItem("sidebar.expanded", next ? "1" : "0"); }
      catch (e) { /* ignore */ }
      return next;
    });
  };
  const sidebarWidthClass = sidebarExpanded ? "w-80" : "w-64";
  const sidebarLeftClass = sidebarExpanded ? "lg:left-80" : "lg:left-64";
  const mainMarginClass = sidebarExpanded ? "lg:ml-80" : "lg:ml-64";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Mobile overlay */}
      {mobileOpen ? <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setMobileOpen(false)} /> : null}
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 ${sidebarWidthClass} z-50 flex flex-col transition-all duration-200 lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} ${sidebarExpanded ? "sidebar-expanded" : "sidebar-compact"}`}
             style={{ background: "#0B1119" }} data-testid="sidebar"
             data-state={sidebarExpanded ? "expanded" : "collapsed"}>
        <div className="px-5 py-5 border-b border-slate-800/60 flex items-center gap-3">
          {/* 2026-08-25 · Logo header aligned 1:1 with PlatformLayout
              so tenant + platform shells present the CertOpz mark
              identically (40×40 max, object-contain, gradient badge
              fallback, exact same typography ladder for the tuple
              beside the mark). */}
          <img
            src={LOGIN_LOGO}
            alt="CertOpz"
            className="object-contain shrink-0"
            style={{ width: "auto", height: "auto",
                     maxWidth: "clamp(160px, 22vw, 280px)",
                     maxHeight: "clamp(40px, 6vw, 72px)" }}
            onError={(e) => { if (!e.currentTarget.src.endsWith("/logo.svg")) e.currentTarget.src = "/logo.svg"; }}
            data-testid="brand-logo"
          />
          <div className="text-white flex-1 min-w-0">
            <div className="font-bold text-2xl leading-none truncate" style={{ fontFamily: "Plus Jakarta Sans" }}
                 title={brand.entitlements?.app_shell_logo ? brand.appDisplayName : "CertOpz"}>
              {brand.entitlements?.app_shell_logo ? (brand.appDisplayName || "CertOpz") : "CertOpz"}
            </div>
            <div className="text-xs tracking-[0.2em] text-teal-400 font-bold uppercase mt-1.5">
              {brand.entitlements?.app_shell_logo && brand.poweredByCertOpz ? "ERP · by CertOpz" : "ERP"}
            </div>
          </div>
          <button className="lg:hidden text-white" onClick={() => setMobileOpen(false)} data-testid="sidebar-close"><X className="w-5 h-5" /></button>
          {/* Desktop-only collapse/expand toggle. Sits in the sidebar
              header so it's always visible (formerly buried in the
              footer). Icon rotates with state; tooltip states action.
              2026-09-05 · UX audit — moved from footer to header. */}
          <button
            type="button"
            onClick={toggleSidebarWidth}
            data-testid="sidebar-width-toggle"
            aria-label={sidebarExpanded ? "Collapse navigation" : "Expand navigation"}
            title={sidebarExpanded ? "Collapse navigation" : "Expand navigation"}
            className="hidden lg:inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-300 hover:text-white hover:bg-white/10 shrink-0 transition"
          >
            {sidebarExpanded ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1" data-testid="sidebar-nav">
          {/* Top fixed items */}
          {NAV_TOP.filter((it) => navAllowedForUser(it, user) && navEntitled(it, sub) && navGroupAllowedForModule(it, user, sub)).map((it) => (
            <NavLink key={it.to} to={it.to} end={it.to === "/"} data-testid={it.testid}
                     onClick={() => setMobileOpen(false)}
                     title={it.label}
                     className={({ isActive }) => `co-sidebar-link ${isActive ? "active" : ""}`}>
              <it.icon className="w-4 h-4 shrink-0" />
              <span className="truncate">{it.label}</span>
            </NavLink>
          ))}
          {/* Grouped items — Phase SIDEBAR-REGROUP: two-level nesting.
              Groups referenced by any NAV_SECTIONS render inside their
              parent section; the rare "orphan" group (not listed under
              any section) still renders at the top level as a safety
              net so nothing disappears if a section entry is misspelled.
              2026-08-25 · When the tenant has BOTH modules and the
              topbar workspace switcher is active, honour the switcher
              choice by rendering only the matching section. Sync the
              preference to the current route so a deep-link into
              /hr/* flips the sidebar even without touching the pill. */}
          <div className="pt-2">
            {(() => {
              const dual = isDualAccess(user);
              // Route-derived preference wins so a direct URL visit
              // (deep link, back button) always agrees with the sidebar.
              const routeIsHr = location.pathname.startsWith("/hr");
              const storedPref = getPreferredWorkspace();
              const preferHr = dual && (routeIsHr || storedPref === "hr");
              const preferCert = dual && !preferHr;
              // Keep localStorage in sync with the route so the next
              // login lands on the correct workspace.
              if (dual && routeIsHr && storedPref !== "hr") setPreferredWorkspace("hr");
              if (dual && !routeIsHr && storedPref !== "certification" && !location.pathname.startsWith("/admin") && !location.pathname.startsWith("/subscription")) {
                setPreferredWorkspace("certification");
              }
              const filteredSections = NAV_SECTIONS.filter((s) => {
                // Plan-level module gate: a section tied to a module the
                // tenant's plan does not include never renders.
                if (s.module && !navGroupAllowedForModule(s, user, sub)) return false;
                if (!dual) return true;                  // single-module tenant: existing gating
                if (s.key === "human_resources") return preferHr;
                if (s.key === "certifications")  return preferCert;
                return true;
              });
              return filteredSections.map((section) => (
                <SidebarSection
                  key={section.key}
                  section={section}
                  allGroups={NAV_GROUPS}
                  user={user}
                  sub={sub}
                  pathname={location.pathname}
                  canHR={canHR}
                  onNavigate={() => setMobileOpen(false)}
                />
              ));
            })()}
            {(() => {
              const referenced = new Set(
                NAV_SECTIONS.flatMap((s) => s.groupKeys || [])
              );
              const orphans = NAV_GROUPS.filter((g) => !referenced.has(g.key));
              return orphans
                .filter((g) => navGroupAllowedForModule(g, user, sub))
                .map((g) => (
                  <SidebarGroup
                    key={g.key}
                    group={g}
                    user={user}
                    sub={sub}
                    pathname={location.pathname}
                    canHR={canHR}
                    onNavigate={() => setMobileOpen(false)}
                  />
                ));
            })()}
          </div>
        </nav>
        <div className="p-3 border-t border-white/5 text-[11px] text-slate-400">
          <span className="truncate block">v1.0 · Lifecycle complete</span>
        </div>
      </aside>

      {/* Topbar */}
      <header className={`fixed top-0 left-0 ${sidebarLeftClass} right-0 h-16 bg-white border-b border-slate-200 z-30 flex items-center justify-between px-4 lg:px-6 transition-all duration-200`} data-testid="topbar">
        <div className="flex items-center gap-3">
          <button className="lg:hidden p-2 rounded-lg hover:bg-slate-100" onClick={() => setMobileOpen(true)} data-testid="sidebar-toggle"><Menu className="w-5 h-5 text-slate-600" /></button>
          <span className="hidden md:inline text-xs uppercase tracking-widest text-slate-400 font-semibold">Workspace</span>
          <span className="hidden sm:inline font-semibold text-slate-700" data-testid="topbar-tenant">{tenant.name || "—"}</span>
          {tenant.fy ? (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide bg-cyan-50 text-cyan-700 font-bold border border-cyan-200 hover:bg-cyan-100 transition flex items-center gap-1"
                  data-testid="topbar-fy"
                  aria-label="Switch financial year"
                >
                  {tenant.fy}
                  <ChevronDown className="w-3 h-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-2" data-testid="fy-switcher-popover">
                <div className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-bold">Financial year · Global filter</div>
                <div className="max-h-64 overflow-y-auto">
                  {fys.length === 0 ? (
                    <div className="px-2 py-3 text-xs text-slate-500">No financial years configured.</div>
                  ) : fys.map((f) => {
                    const active = f.id === activeFyId;
                    return (
                      <button
                        key={f.id}
                        onClick={() => switchFy(f.id)}
                        disabled={fySwitching}
                        className={`w-full text-left px-2 py-1.5 rounded-md flex items-center justify-between gap-2 text-sm ${active ? "bg-cyan-50 text-cyan-800 font-semibold" : "hover:bg-slate-50 text-slate-700"} disabled:opacity-50`}
                        data-testid={`fy-switch-${f.id}`}
                      >
                        <div className="flex flex-col">
                          <span>{f.name}</span>
                          <span className="text-[10px] text-slate-500">{(f.start_date || "").slice(0, 10)} → {(f.end_date || "").slice(0, 10)}</span>
                        </div>
                        {active ? <Check className="w-4 h-4 text-cyan-600" /> : null}
                      </button>
                    );
                  })}
                </div>
                <div className="border-t border-slate-100 mt-1 pt-1 px-2 py-1 text-[10px] text-slate-500">
                  {canManageFY ? "Switching changes the active FY for everyone in your org." : "Read-only · Owner/Admin can change."}
                </div>
              </PopoverContent>
            </Popover>
          ) : null}
          {isDualAccess(user)
            && navGroupAllowedForModule({ module: "certification" }, user, sub)
            && navGroupAllowedForModule({ module: "hr" }, user, sub) ? (
            <WorkspaceSwitcher navigate={navigate} />
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-slate-50" data-testid="user-menu-trigger">
                {user?.avatar_url ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL || ""}${user.avatar_url}${(user.avatar_updated_at ? `?t=${encodeURIComponent(user.avatar_updated_at)}` : "")}`}
                    alt=""
                    className="h-8 w-8 rounded-full object-cover ring-1 ring-slate-200"
                    data-testid="topbar-avatar-img"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-cyan-700 text-white text-xs font-bold flex items-center justify-center">{initials}</div>
                )}
                <div className="text-left hidden sm:block">
                  <div className="text-sm font-semibold text-slate-700 leading-none">{user?.full_name}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider" data-testid="topbar-role-label">
                    {((user?.role_display_labels && user.role_display_labels.length) ? user.role_display_labels : (user?.roles || [])).join(", ")}
                  </div>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400 hidden sm:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setProfileOpen(true)}
                data-testid="user-menu-profile"
              >
                <User className="w-4 h-4 mr-2" /> My Profile
              </DropdownMenuItem>
              {(user?.roles || []).includes("Client User") ? (
                <DropdownMenuItem onClick={() => navigate("/portal")} data-testid="user-menu-portal">
                  <FolderOpen className="w-4 h-4 mr-2" /> Client Portal
                </DropdownMenuItem>
              ) : null}
              {/* Phase USERMENU-RBAC (2026-07-09) — Menu-item visibility
                  mirrors the corresponding page/route guards. `Billing &
                  Plan` uses the canonical `sub.canBilling` helper
                  (Owner || Admin || any BILLING_PERMS). `Admin Settings`
                  is Owner/Admin only — the same policy every /api/admin*
                  endpoint already enforces via `require_perm('admin.*')`.
                  Consultant / Sales / Operations / Finance User etc.
                  see neither item — they see only Sign out. */}
              {sub?.canBilling ? (
                <DropdownMenuItem onClick={() => navigate("/subscription/billing-status")} data-testid="user-menu-billing">
                  <CreditCard className="w-4 h-4 mr-2" /> Billing & Plan
                </DropdownMenuItem>
              ) : null}
              {(user?.roles || []).some((r) => r === "Owner" || r === "Admin") ? (
                <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="user-menu-admin">
                  <Settings2 className="w-4 h-4 mr-2" /> Admin Settings
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onClick={logout} data-testid="user-menu-logout">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Content */}
      <main className={`${mainMarginClass} pt-16 bg-white min-h-screen transition-all duration-200`}>
        {/* Phase SCROLLFIX — `min-w-0` lets flex/grid children shrink so
            wide tables wrapped in `.app-table-scroll` can scroll
            horizontally without forcing the whole page to scroll. */}
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto min-w-0" data-testid="main-content">
          <SubscriptionBanner />
          {children}
        </div>
      </main>
      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
    </div>
  );
}

/**
 * Topbar workspace switcher — dual-access users only.
 *
 * Renders a compact pill next to the FY switcher; opens a popover with
 * two tiles (Certification / HR). Clicking a tile writes the stored
 * workspace preference and navigates via react-router (no full page
 * reload). Not shown to single-domain users.
 */
function WorkspaceSwitcher({ navigate }) {
  const current = getPreferredWorkspace();
  const currentLabel = current === "hr" ? "HR & Payroll" : "Certification";
  const flip = (key, route) => {
    setPreferredWorkspace(key);
    navigate(route);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide bg-teal-50 text-teal-700 font-bold border border-teal-200 hover:bg-teal-100 transition flex items-center gap-1"
          data-testid="topbar-workspace-switcher"
          aria-label="Switch workspace"
        >
          <LayoutGrid className="w-3 h-3" />
          {currentLabel}
          <ChevronDown className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2" data-testid="workspace-switcher-popover">
        <div className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
          Switch workspace
        </div>
        <button
          type="button"
          onClick={() => flip("certification", "/")}
          data-testid="topbar-ws-switch-certification"
          className={`w-full text-left px-2 py-2 rounded-md flex items-center gap-2 text-sm ${current === "certification" ? "bg-teal-50 text-teal-800 font-semibold" : "hover:bg-slate-50 text-slate-700"}`}
        >
          <ScrollText className="w-4 h-4 text-teal-600 shrink-0" />
          <div className="flex-1">
            <div className="leading-tight">Certification ERP</div>
            <div className="text-[10px] text-slate-500">Leads · Cases · Finance</div>
          </div>
          {current === "certification" ? <Check className="w-4 h-4 text-teal-600" /> : null}
        </button>
        <button
          type="button"
          onClick={() => flip("hr", "/hr/dashboard")}
          data-testid="topbar-ws-switch-hr"
          className={`w-full text-left px-2 py-2 rounded-md flex items-center gap-2 text-sm ${current === "hr" ? "bg-cyan-50 text-cyan-800 font-semibold" : "hover:bg-slate-50 text-slate-700"}`}
        >
          <UsersIcon className="w-4 h-4 text-cyan-600 shrink-0" />
          <div className="flex-1">
            <div className="leading-tight">HR &amp; Payroll</div>
            <div className="text-[10px] text-slate-500">Employees · Payroll · Leave</div>
          </div>
          {current === "hr" ? <Check className="w-4 h-4 text-cyan-600" /> : null}
        </button>
        <div className="border-t border-slate-100 mt-1 pt-1 px-2 py-1 text-[10px] text-slate-500">
          Your choice is remembered for next login.
        </div>
      </PopoverContent>
    </Popover>
  );
}
